import { useEffect, useState } from 'react';

/**
 * Hook that manages debounced state
 * If initialValue is provided, manages state internally and returns { value, setValue, debouncedValue }
 * Can also be used to debounce an external value by passing it as initialValue and updating via setValue
 *
 * @param initialValue - Initial value for the state
 * @param delay - Delay in milliseconds (default: 500)
 * @returns Object with value, setValue, and debouncedValue
 */
export function useDebounce<T>(initialValue: T, delay: number = 500) {
  const [value, setValue] = useState<T>(initialValue);
  const [debouncedValue, setDebouncedValue] = useState<T>(initialValue);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [value, delay]);

  return {
    value,
    setValue,
    debouncedValue,
  };
}
